import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import { api, API } from "../lib/api";
import { Copy, Activity, ArrowRight, BarChart3, Code2, Repeat, Pause, Play, StopCircle, Trash2, AlertTriangle, CreditCard } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import ManageLiveCampaign from "./campaign_detail/ManageLiveCampaign";

const SOURCE_COLORS = { internal: "#002EA6", cloudresearch: "#FF3B30", intercept: "#FFB800" };
const SOURCE_LABEL = { internal: "Internal", cloudresearch: "Programmatic", intercept: "Intercept" };

export default function CampaignDetail() {
  const { campaignId } = useParams();
  const nav = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [pulse, setPulse] = useState({ events: [], snapshot: null });
  const [interceptLink, setInterceptLink] = useState(null);
  const [embedSnippet, setEmbedSnippet] = useState(null);
  const [waves, setWaves] = useState(null);
  const [copied, setCopied] = useState(false);
  const esRef = useRef(null);

  const generateEmbed = async () => {
    try {
      const { data } = await api.get(`/campaigns/${campaignId}/embed-snippet`);
      setEmbedSnippet(data.snippet);
    } catch { /* button stays available for retry */ }
  };

  const loadWaves = async () => {
    try {
      const { data } = await api.get(`/campaigns/${campaignId}/waves`);
      setWaves(data);
    } catch { /* longitudinal panel may not be configured */ }
  };

  const launchNextWave = async () => {
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/next-wave`);
      nav(`/client/campaigns/${data.campaign.id}`);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const load = async () => {
    const { data } = await api.get(`/campaigns/${campaignId}`);
    setCampaign(data);
  };

  useEffect(() => { load(); }, [campaignId]);

  useEffect(() => {
    if (!campaign || campaign.status !== "active") return;
    // EventSource with withCredentials=true sends the httpOnly `token` cookie
    // automatically on same-origin SSE. No JWT in JS / no query-param fallback.
    const url = `${API}/campaigns/${campaignId}/pulse`;
    const es = new EventSource(url, { withCredentials: true });
    es.addEventListener("snapshot", (e) => {
      try { setPulse((p) => ({ ...p, snapshot: JSON.parse(e.data) })); } catch { /* malformed snapshot ignored */ }
    });
    es.addEventListener("transcript", (e) => {
      try {
        const evt = JSON.parse(e.data);
        setPulse((p) => ({ ...p, events: [evt, ...p.events].slice(0, 30) }));
      } catch { /* malformed transcript event ignored */ }
    });
    es.addEventListener("complete", () => {
      load();
    });
    es.onerror = () => { /* tolerate intermittent drops */ };
    esRef.current = es;
    return () => es.close();
  }, [campaign?.status, campaignId]);

  const generateInterceptLink = async () => {
    const { data } = await api.post(`/campaigns/${campaignId}/intercept-link`);
    const full = `${window.location.origin}${data.path}`;
    setInterceptLink(full);
    navigator.clipboard?.writeText(full).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  // ============ LIFECYCLE: Pause / Resume / Stop / Force-stop / Delete ============
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [lifecycleMsg, setLifecycleMsg] = useState(null); // {kind:'info'|'error', text:string}
  const [confirm, setConfirm] = useState(null); // {action, title, body, danger:bool}

  // Resume-checkout: a client who bounces out of Stripe's hosted
  // checkout without paying lands back on the dashboard with the
  // campaign in `pending_payment`. This CTA mints a fresh Stripe
  // Checkout Session (server-side, so mode is authoritatively
  // determined by `STRIPE_SECRET_KEY` — not the URL) and redirects
  // the client to the hosted payment page. On success Stripe
  // bounces back to `/payment/success?campaign_id=…` which flips
  // the row to `active`.
  const [payBusy, setPayBusy] = useState(false);
  const [payMsg, setPayMsg] = useState(null);
  const resumeCheckout = async () => {
    setPayBusy(true); setPayMsg(null);
    try {
      const { data } = await api.post("/payments/campaign-checkout", {
        campaign_id: campaignId,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      setPayMsg("Payment provider did not return a checkout link. Please try again.");
    } catch (e) {
      setPayMsg(e.response?.data?.detail || e.message || "Unable to open checkout.");
    } finally {
      setPayBusy(false);
    }
  };

  const callLifecycle = async (path, method = "post") => {
    setLifecycleBusy(true); setLifecycleMsg(null);
    try {
      const fn = method === "delete" ? api.delete : api.post;
      const { data } = await fn(`/campaigns/${campaignId}${path}`);
      if (data?.deleted_campaign) {
        // Hard-delete — kick back to dashboard
        nav("/client");
        return;
      }
      setLifecycleMsg({ kind: "info", text: data?.message || "Done." });
      await load();
    } catch (e) {
      setLifecycleMsg({ kind: "error", text: e.response?.data?.detail || e.message });
    } finally {
      setLifecycleBusy(false);
      setConfirm(null);
    }
  };
  const askPause   = () => setConfirm({ action: () => callLifecycle("/pause"),      title: "Pause campaign?",  body: "New respondents will be blocked from joining. Any respondents who are already mid-survey will be allowed to finish and get paid. You can resume at any time.", cta: "Pause", danger: false });
  const askResume  = () => callLifecycle("/resume");
  const askStop    = () => setConfirm({ action: () => callLifecycle("/stop"),       title: "Stop campaign?",   body: "No new respondents will be accepted. Any respondents already mid-survey will finish and be paid normally. Once everyone finishes, the campaign closes for good. You can still view the executive report afterwards.", cta: "Stop gracefully", danger: true });
  const askForce   = () => setConfirm({ action: () => callLifecycle("/force-stop"), title: "Force-stop now?",  body: "Force-stopping abandons every in-flight interview WITHOUT paying those respondents. Only use this if a stopping campaign has been stuck for hours because someone closed their browser tab.", cta: "Force-stop", danger: true });
  const askDelete  = () => setConfirm({ action: () => callLifecycle("", "delete"),  title: "Delete campaign?", body: "This permanently deletes the campaign, every interview record, and the executive report. This cannot be undone.", cta: "Delete forever", danger: true });

  if (!campaign) return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]"><Navigation /><div className="p-12 text-center text-sm text-[var(--rl-text-secondary)]">Loading…</div></div>
  );

  const pct = campaign.target_completes > 0 ? Math.round((campaign.current_completes / campaign.target_completes) * 100) : 0;
  // Live Pulse Stream + Channel Origin pie surface only external-facing
  // sources. Internal-panel routing was decommissioned when the app
  // consolidated on PureSpectrum + Intercept; the `internal` key is
  // kept in the API response for legacy interview rows but suppressed
  // from the client dashboard so it doesn't render an empty "Internal 0"
  // tile on every campaign.
  const _rawBySource = pulse.snapshot?.by_source || campaign.completes_by_source || {};
  const bySource = {
    cloudresearch: _rawBySource.cloudresearch || 0,
    intercept: _rawBySource.intercept || 0,
  };
  const pieData = Object.entries(bySource).map(([k, v]) => ({ name: SOURCE_LABEL[k] || k, key: k, value: v }));

  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="campaign-detail-page">
      <Navigation />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <button onClick={() => nav("/client")} className="text-sm font-semibold text-[var(--rl-text-secondary)] hover:text-[var(--rl-text)] transition-colors mb-4" data-testid="back-link">← Back to dashboard</button>

        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]">Campaign</div>
            <h1 className="font-display font-black text-4xl tracking-tight mt-2">{campaign.title}</h1>
            <div className="flex items-center gap-3 mt-3">
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)] font-mono" data-testid="campaign-status-label">Status:</span>
              <StatusPill status={campaign.status} />
            </div>
          </div>
          {campaign.status === "filled" && (
            <Link
              to={`/client/campaigns/${campaign.id}/report`}
              className="inline-flex items-center gap-2 bg-[var(--rl-accent)] text-white px-5 py-3 font-semibold hover:bg-[var(--rl-text)] transition-colors"
              data-testid="view-report-btn"
            >
              View Executive Report <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          {campaign.status === "stopped" && (
            <Link
              to={`/client/campaigns/${campaign.id}/report`}
              className="inline-flex items-center gap-2 bg-[var(--rl-text)] text-white px-5 py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors"
              data-testid="view-report-btn-stopped"
            >
              View Executive Report <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          {campaign.status === "active" && (
            <Link
              to={`/client/campaigns/${campaign.id}/observe`}
              className="inline-flex items-center gap-2 bg-[var(--rl-text)] text-white px-5 py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors"
              data-testid="live-observe-btn"
            >
              Open live observation room <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>

        {/* Resume Stripe checkout — visible whenever the campaign is
            waiting on payment (client bounced from Stripe's hosted
            page without completing checkout). Placed right under the
            status header so it's impossible to miss. */}
        {campaign.status === "pending_payment" && (
          <div className="mt-6 bg-yellow-50 border border-yellow-300 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="resume-checkout-banner">
            <div>
              <div className="text-sm font-bold text-yellow-900 flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Payment pending
              </div>
              <div className="text-xs text-yellow-900/80 mt-1">
                Your campaign is ready to launch as soon as payment clears. Total due:{" "}
                <span className="font-mono font-bold">${(campaign.total_budget || 0).toFixed(2)}</span>
              </div>
            </div>
            <button
              onClick={resumeCheckout}
              disabled={payBusy}
              className="inline-flex items-center gap-2 bg-[var(--rl-text)] text-white px-5 py-2.5 text-sm font-bold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-50"
              data-testid="resume-checkout-btn"
            >
              <CreditCard className="w-4 h-4" />
              {payBusy ? "Opening checkout…" : "Pay now"}
            </button>
          </div>
        )}
        {payMsg && (
          <div className="mt-3 text-xs px-3 py-2 bg-[var(--rl-accent)]/10 text-[var(--rl-accent)] border border-[var(--rl-accent)]" data-testid="resume-checkout-msg">
            {payMsg}
          </div>
        )}

        {/* ===== Lifecycle controls (owner only — Pause / Resume / Stop / Delete) ===== */}
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="lifecycle-toolbar">
          {campaign.status === "active" && (
            <button onClick={askPause} disabled={lifecycleBusy} className="inline-flex items-center gap-2 bg-white border border-[var(--rl-border)] text-[var(--rl-text)] px-4 py-2 text-sm font-semibold hover:border-[var(--rl-text)] transition-colors disabled:opacity-50" data-testid="lifecycle-pause-btn">
              <Pause className="w-4 h-4" /> Pause
            </button>
          )}
          {campaign.status === "paused" && (
            <button onClick={askResume} disabled={lifecycleBusy} className="inline-flex items-center gap-2 bg-[var(--rl-success)] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50" data-testid="lifecycle-resume-btn">
              <Play className="w-4 h-4" /> Resume
            </button>
          )}
          {(campaign.status === "active" || campaign.status === "paused") && (
            <button onClick={askStop} disabled={lifecycleBusy} className="inline-flex items-center gap-2 bg-white border border-[var(--rl-accent)] text-[var(--rl-accent)] px-4 py-2 text-sm font-semibold hover:bg-[var(--rl-accent)] hover:text-white transition-colors disabled:opacity-50" data-testid="lifecycle-stop-btn">
              <StopCircle className="w-4 h-4" /> Stop
            </button>
          )}
          {campaign.status === "stopping" && (
            <button onClick={askForce} disabled={lifecycleBusy} className="inline-flex items-center gap-2 bg-[var(--rl-accent)] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50" data-testid="lifecycle-force-stop-btn">
              <AlertTriangle className="w-4 h-4" /> Force-stop now
            </button>
          )}
          {(campaign.status === "draft" || campaign.status === "pending_payment" || campaign.status === "stopped" || campaign.status === "filled") && (
            <button onClick={askDelete} disabled={lifecycleBusy} className="inline-flex items-center gap-2 bg-white border border-[var(--rl-border)] text-[var(--rl-text-secondary)] px-4 py-2 text-sm font-semibold hover:border-[var(--rl-accent)] hover:text-[var(--rl-accent)] transition-colors disabled:opacity-50" data-testid="lifecycle-delete-btn">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
        {lifecycleMsg && (
          <div className={`mt-3 text-xs px-3 py-2 ${lifecycleMsg.kind === "error" ? "bg-[var(--rl-accent)]/10 text-[var(--rl-accent)] border border-[var(--rl-accent)]" : "bg-[var(--rl-brand)]/5 text-[var(--rl-text)] border border-[var(--rl-border)]"}`} data-testid="lifecycle-msg">
            {lifecycleMsg.text}
          </div>
        )}
        {campaign.status === "paused" && (
          <div className="mt-3 text-xs px-3 py-2 bg-yellow-50 border border-yellow-200 text-yellow-900" data-testid="status-banner-paused">
            ⏸ Paused — new respondents are blocked. Click <strong>Resume</strong> to re-open intake. Any respondents mid-survey at the moment you paused are still completing and will be paid.
          </div>
        )}
        {campaign.status === "stopping" && (
          <div className="mt-3 text-xs px-3 py-2 bg-orange-50 border border-orange-200 text-orange-900" data-testid="status-banner-stopping">
            ⏳ Stopping — waiting for in-flight respondents to finish and get paid. The campaign will close automatically once the last one finalizes.
          </div>
        )}
        {campaign.status === "stopped" && (
          <div className="mt-3 text-xs px-3 py-2 bg-[var(--rl-bg-subtle)] border border-[var(--rl-border)] text-[var(--rl-text-secondary)]" data-testid="status-banner-stopped">
            ⏹ Stopped — no further responses will be collected. The executive report below is final.
          </div>
        )}

        {/* Confirm modal for destructive lifecycle actions */}
        {confirm && (
          <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" data-testid="lifecycle-confirm">
            <div className="bg-white border border-[var(--rl-border)] max-w-md w-full p-6 shadow-2xl">
              <h2 className="font-display font-black text-2xl tracking-tight" data-testid="lifecycle-confirm-title">{confirm.title}</h2>
              <p className="text-sm text-[var(--rl-text-secondary)] mt-3 leading-relaxed" data-testid="lifecycle-confirm-body">{confirm.body}</p>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setConfirm(null)} disabled={lifecycleBusy} className="px-4 py-2 text-sm font-semibold text-[var(--rl-text-secondary)] hover:text-[var(--rl-text)] transition-colors disabled:opacity-50" data-testid="lifecycle-confirm-cancel">Cancel</button>
                <button onClick={confirm.action} disabled={lifecycleBusy} className={`px-4 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-50 ${confirm.danger ? "bg-[var(--rl-accent)] hover:bg-[var(--rl-text)]" : "bg-[var(--rl-text)] hover:bg-[var(--rl-brand)]"}`} data-testid="lifecycle-confirm-cta">
                  {lifecycleBusy ? "Working…" : confirm.cta}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manage Live Campaign — PS survey / quota CRUD (owner + admin only) */}
        {campaign.external_provider_id && (
          <div className="mt-6">
            <ManageLiveCampaign campaignId={campaignId} onExternalChange={load} />
          </div>
        )}

        <div className="grid lg:grid-cols-[1fr_360px] gap-6 mt-10">
          {/* Live Pulse */}
          <div>
            <div className="bg-white border border-[var(--rl-border)] p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold">
                  <Activity className="w-4 h-4 text-[var(--rl-success)] animate-pulse" />
                  Live Pulse Stream
                </div>
                <div className="text-xs font-mono">{campaign.current_completes} / {campaign.target_completes} ({pct}%)</div>
              </div>
              <div className="mt-3 h-2 bg-[var(--rl-bg-subtle)]">
                <div className="h-full bg-[var(--rl-brand)]" style={{ width: `${Math.min(pct, 100)}%`, transition: "width 600ms ease" }} />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                {Object.entries(bySource).map(([k, v]) => (
                  <div key={k} className="bg-[var(--rl-bg-subtle)] p-3 text-center border border-[var(--rl-border)]">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: SOURCE_COLORS[k] }}>{SOURCE_LABEL[k] || k}</div>
                    <div className="font-display font-black text-xl mt-1">{v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Anonymized transcript feed</div>
                <div className="mt-3 space-y-3 max-h-[420px] overflow-y-auto" data-testid="transcript-feed">
                  {pulse.events.length === 0 ? (
                    <div className="text-sm text-[var(--rl-text-secondary)] italic py-6 text-center">
                      Our AI is currently conducting local interviews. Your boardroom report will compile the moment data collection hits 100%.
                    </div>
                  ) : pulse.events.map((evt, i) => (
                    <div key={`pulse-${evt.ts || ""}-${i}`} className="border-l-2 pl-4 py-1" style={{ borderColor: SOURCE_COLORS[evt.source] || "#E5E5EA" }} data-testid={`transcript-item-${i}`}>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: SOURCE_COLORS[evt.source] }}>{SOURCE_LABEL[evt.source] || evt.source}</div>
                      <div className="text-sm mt-1 leading-relaxed">"{evt.snippet}"</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Side */}
          <div className="space-y-4">
            <div className="bg-white border border-[var(--rl-border)] p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Channel Origin</div>
              <div className="mt-4 h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" innerRadius={45} outerRadius={70} paddingAngle={2}>
                      {pieData.map((e) => <Cell key={e.key} fill={SOURCE_COLORS[e.key]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Programmatic Panel status — surfaces external_provider_id +
                external_provider_status (Live/Paused/Closed/Archived) so the
                client can see whether their CR project is provisioned and
                accepting respondents. Only shown once a project_id exists. */}
            {campaign.external_provider_id && (
              <div className="bg-white border border-[var(--rl-border)] p-6" data-testid="provider-status-card">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Programmatic Panel Project</div>
                  <ExternalProviderBadge status={campaign.external_provider_status} />
                </div>
                <div className="mt-3 text-[11px] text-[var(--rl-text-secondary)] font-mono break-all" data-testid="provider-id">
                  Project ID: {campaign.external_provider_id}
                </div>
                <div className="mt-2 text-[11px] text-[var(--rl-text-secondary)] leading-relaxed">
                  External participant routing is mirrored on our upstream panel. Pause / Resume / Stop actions on this campaign auto-sync the project status.
                </div>
              </div>
            )}

            <div className="bg-white border border-[var(--rl-border)] p-6">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Direct Distribution</div>
                <BarChart3 className="w-4 h-4 text-[var(--rl-brand)]" />
              </div>
              <button
                onClick={generateInterceptLink}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[var(--rl-text)] text-white py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors text-sm"
                data-testid="generate-intercept-link-btn"
              >
                <Copy className="w-4 h-4" /> Generate Direct Distribution Link
              </button>
              {interceptLink && (
                <div className="mt-4 text-xs font-mono break-all bg-[var(--rl-bg-subtle)] p-3 border border-[var(--rl-border)]" data-testid="intercept-link-display">
                  {interceptLink}
                </div>
              )}
              {copied && <div className="mt-2 text-xs font-semibold text-[var(--rl-success)]">Link copied!</div>}
              <p className="mt-3 text-xs text-[var(--rl-text-secondary)] leading-relaxed">Blast to your own customer email lists or embed in live digital environments.</p>
            </div>

            {/* Wave 4: Brand Intercept SDK embed snippet */}
            <div className="bg-white border border-[var(--rl-border)] p-6" data-testid="sdk-embed-block">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Brand Intercept SDK</div>
                <Code2 className="w-4 h-4 text-[var(--rl-brand)]" />
              </div>
              {!embedSnippet ? (
                <button
                  onClick={generateEmbed}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[var(--rl-text)] text-white py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors text-sm"
                  data-testid="generate-embed-btn"
                >
                  <Code2 className="w-4 h-4" /> Generate embed code
                </button>
              ) : (
                <pre className="mt-4 text-[10px] font-mono break-all whitespace-pre-wrap bg-[var(--rl-bg-subtle)] p-3 border border-[var(--rl-border)] max-h-40 overflow-auto" data-testid="embed-snippet-display">{embedSnippet}</pre>
              )}
              <p className="mt-3 text-xs text-[var(--rl-text-secondary)] leading-relaxed">Drop this on your own site to deploy the interview to your logged-in users.</p>
            </div>

            {/* Wave 4: Longitudinal study waves */}
            {(campaign.recurrence_cadence && campaign.recurrence_cadence !== "none") && (
              <div className="bg-white border border-[var(--rl-border)] p-6" data-testid="longitudinal-block">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Longitudinal study</div>
                  <Repeat className="w-4 h-4 text-[var(--rl-brand)]" />
                </div>
                {!waves ? (
                  <button onClick={loadWaves} className="mt-4 w-full bg-[var(--rl-bg-subtle)] text-[var(--rl-text)] py-3 font-semibold text-sm border border-[var(--rl-border)]" data-testid="load-waves-btn">View wave history</button>
                ) : (
                  <div className="mt-4 space-y-2">
                    {waves.waves.map((w) => (
                      <div key={w.id} className="flex items-center justify-between text-sm border-b border-[var(--rl-border)] pb-2" data-testid={`wave-${w.wave_number}`}>
                        <div>
                          <span className="font-mono text-xs text-[var(--rl-accent)] mr-2">W{w.wave_number}</span>
                          {w.title}
                        </div>
                        <div className="text-xs font-mono text-[var(--rl-text-secondary)]">{w.current_completes}/{w.target_completes}</div>
                      </div>
                    ))}
                  </div>
                )}
                <button onClick={launchNextWave} className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[var(--rl-accent)] text-white py-3 font-semibold hover:bg-[var(--rl-text)] transition-colors text-sm" data-testid="launch-next-wave-btn">
                  <Repeat className="w-4 h-4" /> Launch next wave →
                </button>
                <p className="mt-3 text-xs text-[var(--rl-text-secondary)] leading-relaxed">Re-survey the same cohort with the same questions on the configured cadence.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const STYLE = {
    active:          { bg: "var(--rl-success)",   fg: "#fff",   label: "Active" },
    paused:          { bg: "#FFC107",             fg: "#111",   label: "Paused" },
    stopping:        { bg: "#FF9800",             fg: "#fff",   label: "Stopping" },
    stopped:         { bg: "var(--rl-text-secondary)", fg: "#fff", label: "Stopped" },
    filled:          { bg: "var(--rl-brand)",     fg: "#fff",   label: "Filled" },
    draft:           { bg: "var(--rl-border)",    fg: "var(--rl-text)", label: "Draft" },
    pending_payment: { bg: "var(--rl-border)",    fg: "var(--rl-text)", label: "Pending payment" },
  };
  const s = STYLE[status] || { bg: "var(--rl-border)", fg: "var(--rl-text)", label: status };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-bold"
      style={{ background: s.bg, color: s.fg }}
      data-testid={`campaign-status-pill-${status}`}
    >
      {s.label}
    </span>
  );
}


function ExternalProviderBadge({ status }) {
  // Mirrors CR project lifecycle states: Unpublished | Live | Paused | Closed | Archived.
  // Defaults to "Provisioning…" when the backend has a project_id but no status yet
  // (the CR /update-status call to Live runs immediately after project creation but
  // is best-effort and can lag a few hundred ms on a slow round-trip).
  const norm = (status || "").toString();
  const STYLE = {
    Live:        { bg: "var(--rl-success)",        fg: "#fff", label: "Live", dot: true },
    Paused:      { bg: "#FFC107",                  fg: "#111", label: "Paused" },
    Closed:      { bg: "var(--rl-text-secondary)", fg: "#fff", label: "Closed" },
    Archived:    { bg: "var(--rl-border)",         fg: "var(--rl-text)", label: "Archived" },
    Unpublished: { bg: "var(--rl-border)",         fg: "var(--rl-text)", label: "Unpublished" },
  };
  const s = STYLE[norm] || { bg: "var(--rl-border)", fg: "var(--rl-text)", label: norm || "Provisioning…" };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-bold"
      style={{ background: s.bg, color: s.fg }}
      data-testid={`provider-status-pill-${norm || "provisioning"}`}
    >
      {s.dot && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
      {s.label}
    </span>
  );
}
